import type { WoodpeckerConfig } from 'codesearch-backend'
import { For, Show } from 'solid-js'
import { Badge } from './ui/Badge.tsx'

export interface WoodpeckerViewProps {
  config: WoodpeckerConfig
}

function formatFilterVal(val: unknown, name: string): string[] {
  if (!val) return []
  if (typeof val === 'string') return [`${name}: ${val}`]
  if (Array.isArray(val)) return val.map(v => `${name}: ${v}`)
  if (typeof val === 'object' && val !== null) {
    const obj = val as { include?: unknown, exclude?: unknown }
    const out: string[] = []
    if (obj.include) {
      const incs = Array.isArray(obj.include) ? obj.include : [obj.include]
      out.push(...incs.map(i => `${name}: ${i}`))
    }
    if (obj.exclude) {
      const excs = Array.isArray(obj.exclude) ? obj.exclude : [obj.exclude]
      out.push(...excs.map(e => `${name}: !${e}`))
    }
    return out
  }
  return []
}

export function WoodpeckerView(props: WoodpeckerViewProps) {
  const cfg = () => props.config

  const labels = () => cfg().labels ?? {}
  const rawSteps = () => cfg().steps ?? cfg().pipeline ?? []
  const steps = () => {
    const raw = rawSteps()
    if (Array.isArray(raw)) return raw
    return Object.entries(raw).map(([key, s]) => ({ name: s.name || key, ...s }))
  }

  const rawServices = () => cfg().services ?? []
  const services = () => {
    const raw = rawServices()
    if (Array.isArray(raw)) return raw
    return Object.entries(raw).map(([key, s]) => ({ name: s.name || key, ...s }))
  }

  const cloneSteps = () => {
    const clone = cfg().clone
    if (!clone || typeof clone === 'boolean') return []
    if (Array.isArray(clone)) return clone
    return [clone as any]
  }

  const matrix = () => cfg().matrix ?? {}

  return (
    <div class="h-full overflow-auto bg-background p-6 space-y-6">
      {/* Top Banner / Summary Card */}
      <div class="border border-border rounded-xl bg-card p-5 shadow-sm space-y-4">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <span class="i-lucide-layers size-6 text-blue-500" />
              <h1 class="text-xl font-semibold tracking-tight">Woodpecker CI Pipeline</h1>
            </div>
            <p class="text-xs text-muted-foreground">
              Pipeline with
              {' '}
              {steps().length}
              {' '}
              steps
            </p>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <For each={Object.entries(labels())}>
              {([k, v]) => (
                <Badge variant="secondary" class="gap-1 text-xs font-mono">
                  <span class="text-muted-foreground">
                    {k}
                    :
                  </span>
                  <span>{v}</span>
                </Badge>
              )}
            </For>
            <Show when={cfg().depends_on?.length}>
              <Badge variant="outline" class="gap-1 text-xs">
                <span>Depends on:</span>
                <span>{cfg().depends_on?.join(', ')}</span>
              </Badge>
            </Show>
          </div>
        </div>

        {/* Matrix variables if defined */}
        <Show when={Object.keys(matrix()).length > 0}>
          <div class="flex flex-wrap items-center gap-2 border-t border-border/50 pt-3 text-xs">
            <span class="text-muted-foreground font-medium">Build Matrix:</span>
            <For each={Object.entries(matrix())}>
              {([mKey, mVals]) => (
                <Badge variant="outline" class="border-purple-500/20 bg-purple-500/10 text-xs text-purple-600 font-mono dark:text-purple-400">
                  <span>
                    {mKey}
                    :
                  </span>
                  <span>
                    [
                    {mVals.join(', ')}
                    ]
                  </span>
                </Badge>
              )}
            </For>
          </div>
        </Show>
      </div>

      {/* Custom Clone Steps */}
      <Show when={cloneSteps().length > 0}>
        <div class="space-y-3">
          <div class="flex items-center gap-2 text-sm text-foreground font-semibold">
            <span class="i-lucide-git-branch size-4 text-purple-500" />
            <h2>Clone Configuration</h2>
          </div>
          <div class="grid grid-cols-1 gap-3">
            <For each={cloneSteps()}>
              {cs => (
                <div class="border border-border/80 rounded-lg bg-card p-3.5 text-xs space-y-2">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2 text-foreground font-medium">
                      <span>{cs.name || 'clone'}</span>
                      <Show when={cs.image}>
                        <Badge variant="outline" class="text-2xs font-mono">{cs.image}</Badge>
                      </Show>
                    </div>
                  </div>
                  <Show when={cs.settings && Object.keys(cs.settings).length > 0}>
                    <div class="flex flex-wrap items-center gap-2 text-2xs text-muted-foreground font-mono">
                      <span>Settings:</span>
                      <For each={Object.entries(cs.settings!)}>
                        {([sk, sv]) => (
                          <span class="rounded bg-muted px-1.5 py-0.5">
                            {sk}
                            =
                            {String(sv)}
                          </span>
                        )}
                      </For>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* Pipeline Steps */}
      <div class="space-y-4">
        <div class="flex items-center gap-2 text-base text-foreground font-semibold">
          <span class="i-lucide-play size-5 text-emerald-500" />
          <h2>
            <span>Pipeline Steps (</span>
            <span>{steps().length}</span>
            <span>)</span>
          </h2>
        </div>

        <div class="grid grid-cols-1 gap-4">
          <For each={steps()}>
            {(step, idx) => {
              const cmds = () => {
                if (!step.commands) return []
                return Array.isArray(step.commands) ? step.commands : [step.commands]
              }

              const envEntries = () => Object.entries(step.environment ?? {})

              const stepDeps = () => {
                if (!step.depends_on) return []
                return Array.isArray(step.depends_on) ? step.depends_on : [step.depends_on]
              }

              const whenEvents = () => {
                if (!step.when) return []
                const whenArr = Array.isArray(step.when) ? step.when : [step.when]
                const out: string[] = []
                for (const cond of whenArr) {
                  out.push(...formatFilterVal(cond.event, 'event'))
                  out.push(...formatFilterVal(cond.branch, 'branch'))
                  out.push(...formatFilterVal(cond.path, 'path'))
                  out.push(...formatFilterVal(cond.platform, 'platform'))
                  out.push(...formatFilterVal(cond.status, 'status'))
                }
                return out
              }

              return (
                <div class="border border-border/80 rounded-xl bg-card p-4 shadow-sm space-y-3">
                  <div class="flex flex-wrap items-center justify-between gap-3">
                    <div class="flex items-center gap-2.5">
                      <div class="size-6 flex items-center justify-center rounded-full bg-muted text-xs text-muted-foreground font-semibold">
                        {idx() + 1}
                      </div>
                      <span class="text-sm text-foreground font-semibold">
                        {step.name || `step-${idx() + 1}`}
                      </span>
                      <Show when={step.image}>
                        <Badge variant="outline" class="bg-muted/40 text-2xs font-mono">
                          {step.image}
                        </Badge>
                      </Show>
                    </div>

                    <div class="flex flex-wrap items-center gap-1.5">
                      <Show when={stepDeps().length > 0}>
                        <Badge variant="outline" size="sm" class="gap-1 text-2xs">
                          <span>needs:</span>
                          <span>{stepDeps().join(', ')}</span>
                        </Badge>
                      </Show>
                      <Show when={step.privileged}>
                        <Badge variant="destructive" size="sm">Privileged</Badge>
                      </Show>
                      <Show when={step.detach}>
                        <Badge variant="secondary" size="sm">Background (detach)</Badge>
                      </Show>
                      <For each={whenEvents()}>
                        {w => (
                          <Badge variant="default" size="sm" class="border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                            {w}
                          </Badge>
                        )}
                      </For>
                    </div>
                  </div>

                  {/* Commands */}
                  <Show when={cmds().length > 0}>
                    <div class="overflow-x-auto rounded-lg bg-muted/50 p-3 text-xs font-mono space-y-1.5">
                      <For each={cmds()}>
                        {cmd => (
                          <div class="text-foreground/90 leading-relaxed">
                            <span>$ </span>
                            <span>{cmd}</span>
                          </div>
                        )}
                      </For>
                    </div>
                  </Show>

                  {/* Environment & Volumes */}
                  <div class="flex flex-wrap items-start gap-4 text-xs">
                    <Show when={envEntries().length > 0}>
                      <div class="space-y-1">
                        <span class="text-2xs text-muted-foreground font-medium tracking-wider uppercase">Environment</span>
                        <div class="flex flex-wrap gap-1.5">
                          <For each={envEntries()}>
                            {([k, v]) => {
                              const isSecret = typeof v === 'object' && v !== null && 'from_secret' in v
                              return (
                                <span class="inline-flex items-center gap-1 border border-border/60 rounded bg-muted/30 px-2 py-0.5 text-2xs font-mono">
                                  <span>{k}</span>
                                  <span>=</span>
                                  {isSecret
                                    ? (
                                        <span class="flex items-center gap-0.5 text-amber-500 font-semibold">
                                          <span class="i-lucide-key size-3" />
                                          <span>{(v as any).from_secret}</span>
                                        </span>
                                      )
                                    : (
                                        <span class="text-muted-foreground">{String(v)}</span>
                                      )}
                                </span>
                              )
                            }}
                          </For>
                        </div>
                      </div>
                    </Show>

                    <Show when={step.volumes?.length}>
                      <div class="space-y-1">
                        <span class="text-2xs text-muted-foreground font-medium tracking-wider uppercase">Volumes</span>
                        <div class="flex flex-wrap gap-1.5">
                          <For each={step.volumes}>
                            {vol => (
                              <span class="border border-border/60 rounded bg-muted/30 px-2 py-0.5 text-2xs text-muted-foreground font-mono">
                                {vol}
                              </span>
                            )}
                          </For>
                        </div>
                      </div>
                    </Show>
                  </div>
                </div>
              )
            }}
          </For>
        </div>
      </div>

      {/* Services if defined */}
      <Show when={services().length > 0}>
        <div class="pt-2 space-y-4">
          <div class="flex items-center gap-2 text-base text-foreground font-semibold">
            <span class="i-lucide-database size-5 text-purple-500" />
            <h2>
              <span>Sidecar Services (</span>
              <span>{services().length}</span>
              <span>)</span>
            </h2>
          </div>

          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <For each={services()}>
              {svc => (
                <div class="border border-border/80 rounded-xl bg-card p-4 shadow-sm space-y-2">
                  <div class="flex items-center justify-between">
                    <span class="text-sm text-foreground font-semibold">{svc.name}</span>
                    <Show when={svc.image}>
                      <Badge variant="outline" class="text-2xs font-mono">{svc.image}</Badge>
                    </Show>
                  </div>
                  <Show when={svc.ports?.length}>
                    <div class="text-xs text-muted-foreground">
                      <span>Ports: </span>
                      <span class="text-foreground font-mono">{svc.ports?.join(', ')}</span>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  )
}
